import {
	CheckboxField,
	FormDrawerContentProps,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	MultiSelectField,
	Separator,
	SupportedLanguageSingleSelectField,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { useFormikContext } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { visibilityOptions } from "../../../shared/types/event";
import { eventTypeService } from "../shared/api/eventTypeService";
import { EventRoleSingleSelectField } from "../shared/select/EventRoleSingleSelectField";
import { EventRole, EventTypeFormValues, EventTypeTranslationEntry } from "../shared/types/eventType";
import { EventTypeImagePicker } from "./EventTypeImagePicker";
import { EventTypeSettingsFormFields } from "./EventTypeSettingsFormFields";
import { eventTypeValidation } from "./eventTypeValidation";
import { FeedbackMultiSelectField } from "./FeedbackMultiSelectField";

const StyledI18nFormFetchDrawer = styled(I18nFormFetchDrawer<EventTypeFormValues>)`
	width: 600px;
	max-width: 50vw;
`;

const emptyTranslation: EventTypeTranslationEntry = {
	name: "",
};

const EventTypeDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext<EventTypeFormValues>();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<EventRoleSingleSelectField
					label={t("Event role")}
					name="eventRole"
					required={true}
					description={t("Determines the role our company has at this event.")}
					onChange={(event) =>
						event.target.value !== EventRole.ATTENDEE && setFieldValue("requiresParticipationApproval", false)
					}
				/>
				{values.eventRole === EventRole.ATTENDEE && (
					<CheckboxField
						label={t("Enable participant approval")}
						name="requiresParticipationApproval"
						infoText={t("When enabled, each participant must be approved before joining.")}
					/>
				)}
				<SupportedLanguageSingleSelectField label={t("Fallback language")} name="fallbackLanguage" required />
				<FeedbackMultiSelectField name="feedback" label={t("Feedback")} clearable />
				<TextField type="color" name="color" label={t("Color")} />
			</I18nOnlyOnMainForm>
			<EventTypeImagePicker label={t("Image")} name="image" />
			<I18nOnlyOnMainForm>
				<CheckboxField label={t("Mark as default")} name="isDefault" />
				<CheckboxField label={t("All users can edit events of this type")} name="isEventEditableByAllUsers" />
				<CheckboxField label={t("Show feedback evaluation")} name="showFeedbackEvaluation" />
				<MultiSelectField
					name="allowedVisibilities"
					label={t("Visibility")}
					required={true}
					options={visibilityOptions}
					getLabel={(v) => t(`eventVisibility.${v}`)}
					getValue={(v) => v}
				/>
				<Separator />
				<EventTypeSettingsFormFields showInfo={false} />
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const EventTypeUpdateDrawer: FC<UpdateDrawerFormProps<EventTypeFormValues>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = eventTypeValidation(t);

	return (
		<StyledI18nFormFetchDrawer
			{...drawerProps}
			validate={validate}
			queryFn={() => eventTypeService.show(drawerProps.id).then(({ data }) => data.data)}
			queryKey={eventTypeService.showKey(drawerProps.id)}
			title={t("Edit event type")}
			renderSuccess={(props) => <EventTypeDrawerContent {...props} />}
		/>
	);
};
